import React from 'react';
import css from './HumanPullArt.module.scss';

/**
 * The "pull track" hero illustration, hand-built as SVG so the green line can
 * draw itself on load (stroke-dashoffset) and every colour ties to the brand
 * tokens. A single confident green line leaves the user (left dot), weaves past
 * a faint grid of faceless profile cards (the pile an algorithm screens), and
 * arrives at one warm amber person: the hiring manager. Decorative.
 */

const GREEN = '#207551';
const AMBER = '#faad14';
const GRID = '#e2e8f0';

// Faint grid of faceless résumé/profile cards — the automated pile.
const COLS = [392, 462, 532, 602, 672, 742, 812];
const ROWS = [60, 132, 204, 276];
const CARD_W = 50;
const CARD_H = 60;

// One continuous weaving line that threads the gutters BETWEEN the cards (never
// over them): horizontals sit in the row gaps (y 126/198/270), verticals in the
// column gaps (x 452/592/732). pathLength={1} normalises it so the draw
// animation is a simple 1 -> 0 dashoffset regardless of real length.
const LINE =
  'M 22 198 ' +
  'L 452 198 ' +
  'L 452 126 ' +
  'L 592 126 ' +
  'L 592 270 ' +
  'L 732 270 ' +
  'L 732 196 ' +
  'L 872 196';

const NODE = { cx: 910, cy: 196, r: 38 };

// Eight short rays around the amber person — the brightest point in the frame.
const RAYS = Array.from({ length: 8 }, (_, i) => {
  const angle = (i / 8) * Math.PI * 2;
  const inner = NODE.r + 8;
  const outer = NODE.r + 18;
  return {
    x1: NODE.cx + Math.cos(angle) * inner,
    y1: NODE.cy + Math.sin(angle) * inner,
    x2: NODE.cx + Math.cos(angle) * outer,
    y2: NODE.cy + Math.sin(angle) * outer,
  };
});

const HumanPullArt: React.FC = () => (
  <svg
    className={css.art}
    viewBox="0 0 980 356"
    fill="none"
    aria-hidden="true"
    focusable="false"
    preserveAspectRatio="xMidYMid meet"
  >
    {/* The pile */}
    <g className={css.grid} aria-hidden="true">
      {ROWS.map((y) =>
        COLS.map((x) => (
          <g key={`${x}-${y}`}>
            <rect x={x} y={y} width={CARD_W} height={CARD_H} rx="6" stroke={GRID} strokeWidth="2" />
            <circle cx={x + CARD_W / 2} cy={y + 18} r="7" stroke={GRID} strokeWidth="2" />
            <path
              d={`M ${x + 12} ${y + 40} a ${CARD_W / 2 - 12} ${CARD_W / 2 - 12} 0 0 1 ${CARD_W - 24} 0`}
              stroke={GRID}
              strokeWidth="2"
            />
            <line x1={x + 12} y1={y + 50} x2={x + CARD_W - 12} y2={y + 50} stroke={GRID} strokeWidth="2" />
          </g>
        ))
      )}
    </g>

    {/* You */}
    <circle cx="22" cy="198" r="7" fill={GREEN} aria-hidden="true" />

    {/* The pull line */}
    <path
      className={css.line}
      d={LINE}
      pathLength={1}
      stroke={GREEN}
      strokeWidth="3.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    />

    {/* The hiring manager — the one warm note */}
    <g className={css.node} aria-hidden="true">
      {RAYS.map((r, i) => (
        <line key={i} x1={r.x1} y1={r.y1} x2={r.x2} y2={r.y2} stroke={AMBER} strokeWidth="2.5" strokeLinecap="round" />
      ))}
      <circle cx={NODE.cx} cy={NODE.cy} r={NODE.r} fill="#fff" stroke={AMBER} strokeWidth="3" />
      <circle cx={NODE.cx} cy={NODE.cy - 9} r="10" stroke={AMBER} strokeWidth="3" />
      <path
        d={`M ${NODE.cx - 16} ${NODE.cy + 18} a 16 16 0 0 1 32 0`}
        stroke={AMBER}
        strokeWidth="3"
        strokeLinecap="round"
      />
    </g>
  </svg>
);

export default HumanPullArt;
